"use client"

import * as React from "react"
import { COLOR_ETIQUETA_POR_DEFECTO } from "@/lib/colores-de-etiqueta"
import { X, Search } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export interface Tag {
  id: string
  name: string
  color?: string
  tag_type?: string
  description?: string
}

export interface TagSelectorProps {
  availableTags: Tag[]
  selectedTags: Tag[]
  onTagsChange: (tags: Tag[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  maxTags?: number
  allowCreate?: boolean
  onCreateTag?: (tag: { name: string; color: string }) => Promise<Tag>
  size?: "sm" | "md" | "lg"
  variant?: 'default' | 'outline'
}

export function TagSelector({
  availableTags,
  selectedTags,
  onTagsChange,
  placeholder,
  className,
  disabled = false,
  maxTags,
  size = "md",
  variant = "default",
}: TagSelectorProps) {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = React.useState("")

  // Filter out already selected tags
  const unselectedTags = React.useMemo(() => {
    const selectedIds = new Set(selectedTags.map(tag => tag.id))
    return availableTags.filter(tag => !selectedIds.has(tag.id))
  }, [availableTags, selectedTags])

  const handleSelectTag = (tag: Tag) => {
    if (maxTags && selectedTags.length >= maxTags) {
      return // Don't allow more than maxTags
    }
    
    const newTags = [...selectedTags, tag]
    onTagsChange(newTags)
  }

  const handleRemoveTag = (tagId: string) => {
    const newTags = selectedTags.filter(tag => tag.id !== tagId)
    onTagsChange(newTags)
  }

  const searchFilteredTags = React.useMemo(() => {
    return unselectedTags.filter(tag => 
      tag.name.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [unselectedTags, searchValue])

  return (
    <div className={cn("space-y-2", className)}>
      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className="space-y-1">
          <Label className="text-sm font-medium">{t("tag_selector.selected_tags")}</Label>
          <div className="flex min-h-[28px] flex-wrap gap-1 rounded-md border bg-muted/20 p-2">
            {selectedTags.map((tag) => (
              <Badge
                key={tag.id}
            variant={variant}
                className={cn(
                  "flex items-center gap-1",
                  size === "sm" && "text-xs px-1 py-0",
                  size === "md" && "text-sm px-2 py-1",
                  size === "lg" && "text-base px-3 py-1"
                )}
              >
                <div
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: tag.color || COLOR_ETIQUETA_POR_DEFECTO }}
                />
                {tag.name}
                {!disabled && (
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive" 
                    onClick={() => handleRemoveTag(tag.id)}
                  />
                )}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="space-y-1">
        <Label className="text-sm font-medium">{t("tag_selector.add_tags")}</Label>
              <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
                  placeholder={placeholder || t("tag_selector.search_tags_placeholder")}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
            className="pl-8"
                />
              </div>
      </div>

      {/* Available Tags List */}
      <div className="space-y-1">
        <Label className="text-sm font-medium">{t("tag_selector.available_tags")}</Label>
        <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
          {searchFilteredTags.length > 0 ? (
            searchFilteredTags.map((tag) => (
              <div
                key={tag.id}
                className="flex cursor-pointer items-center space-x-2 rounded-md p-2 hover:bg-muted"
                onClick={() => handleSelectTag(tag)}
              >
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: tag.color || COLOR_ETIQUETA_POR_DEFECTO }}
                />
                <span className="text-sm">{tag.name}</span>
              </div>
            ))
          ) : (
            <div className="p-2 text-sm text-muted-foreground">
              {searchValue
                ? t("tag_selector.no_tags_found_matching_search")
                : t("tag_selector.no_available_tags")}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
